<template>
  <div>
    <div class="crm-flowbar">
      <router-link class="crm-back" :to="backTo">{{ backLabel }}</router-link>
    </div>
    <div v-if="loading" class="crm-hint">Chargement…</div>
    <div v-else-if="!fiche" class="crm-hint">Fiche introuvable</div>
    <div v-else>
      <div class="crm-hero-navy">
        <p class="crm-kicker">Fiche contact</p>
        <h1 class="crm-title" style="margin-top:4px">{{ name }}</h1>
        <p class="crm-hint">{{ fiche.societe }} · {{ fiche.poste || '—' }}</p>
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:4px">
          <span class="crm-badge">{{ fiche.relation }}</span>
          <span class="crm-badge ok">{{ fiche.pipeline }}</span>
          <span v-if="fiche.origin" class="crm-badge">{{ fiche.origin }}</span>
          <span v-for="src in sourceFlags" :key="src" class="crm-badge">{{ sourceFlagLabel(src) }}</span>
          <span v-if="fiche.statut_monday" class="crm-badge">Monday · {{ fiche.statut_monday }}</span>
        </div>
        <div class="crm-hero-actions">
          <a v-if="fiche.telephone" class="crm-btn crm-btn-primary" :href="'tel:' + fiche.telephone">Appeler</a>
          <a v-if="fiche.email" class="crm-btn crm-btn-outline" :href="'mailto:' + fiche.email" style="background:rgba(255,255,255,.1);color:#fffaf2;box-shadow:none">Écrire</a>
        </div>
      </div>

      <div class="crm-card" v-if="fiche.carte_url">
        <label class="crm-label">Carte / badge</label>
        <a :href="fiche.carte_url" target="_blank" rel="noopener">
          <img :src="fiche.carte_url" class="crm-img-carte" alt="Carte" />
        </a>
      </div>

      <div class="crm-card">
        <h3 class="crm-section-title">Coordonnées</h3>
        <div class="crm-field"><label class="crm-label">Téléphone</label><a :href="'tel:' + fiche.telephone">{{ fiche.telephone || '—' }}</a></div>
        <div class="crm-field"><label class="crm-label">Email</label><a :href="'mailto:' + fiche.email">{{ fiche.email || '—' }}</a></div>
        <div class="crm-field"><label class="crm-label">ORIAS</label><span>{{ fiche.orias || '—' }}</span></div>
        <div class="crm-field" style="margin-bottom:0"><label class="crm-label">Ville</label><span>{{ [fiche.cp, fiche.ville].filter(Boolean).join(' ') || '—' }}</span></div>
      </div>

      <div class="crm-card" v-if="hasClientEco">
        <h3 class="crm-section-title">Note flash — CA & commandes</h3>
        <div class="crm-grid-2" style="margin-bottom:12px">
          <div class="crm-stat"><strong>{{ fmtEuro(fiche.ca_global) }}</strong><span>CA global</span></div>
          <div class="crm-stat"><strong>{{ fiche.orders_count || 0 }}</strong><span>Commandes</span></div>
        </div>
        <p class="crm-hint">Dernière commande : {{ fmtDate(fiche.last_order) }} · Client depuis {{ fiche.first_year || '—' }}</p>
        <div v-if="(fiche.orders || []).length">
          <label class="crm-label">Dernières commandes</label>
          <div v-for="o in (fiche.orders || []).slice(0,5)" :key="o.id" class="crm-list-row">
            <div style="flex:1" class="crm-row-title">{{ fmtDate(o.date) }} · {{ o.product }}</div>
            <strong>{{ fmtEuro(o.ca) }}</strong>
          </div>
        </div>
        <div v-else class="crm-hint" style="margin-bottom:0">Aucune commande listée.</div>
      </div>

      <div class="crm-card" v-if="!salonModeResolved">
        <h3 class="crm-section-title">Scoring (interne)</h3>
        <p class="crm-hint" style="margin-bottom:0">
          Score {{ fiche.score_manuel || '—' }}/5 · Copilote {{ fiche.copilot_score || '—' }}/5 ({{ fiche.copilot_verdict || '—' }})
        </p>
      </div>

      <div class="crm-card">
        <label class="crm-label">Points évoqués / présence salon</label>
        <p class="crm-hint">{{ fiche.produits_monday || fiche.remarque || '—' }}</p>
        <label class="crm-label">À retenir (interne)</label>
        <p class="crm-note" style="white-space:pre-wrap">{{ fiche.note_interne || '—' }}</p>
        <p v-if="fiche.todo_action" class="crm-hint" style="margin-top:8px">
          Action ouverte : {{ fiche.todo_action }}
        </p>
        <div class="crm-field" style="margin-top:12px;margin-bottom:0">
          <label class="crm-label">Ajouter une note</label>
          <textarea
            class="crm-textarea"
            v-model="commentDraft"
            rows="3"
            placeholder="Commentaire simple, ou action pour À faire…"
          ></textarea>
          <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px">
            <button
              type="button"
              class="crm-btn crm-btn-outline"
              :disabled="commentSaving || !commentDraft.trim()"
              @click="addComment(false)"
            >{{ commentSaving ? '…' : 'Commentaire' }}</button>
            <button
              type="button"
              class="crm-btn crm-btn-primary"
              :disabled="commentSaving || !commentDraft.trim()"
              @click="addComment(true)"
            >{{ commentSaving ? '…' : 'Action' }}</button>
          </div>
        </div>
      </div>

      <div class="crm-card" v-if="activityRows.length">
        <h3 class="crm-section-title">Activité</h3>
        <div v-for="a in activityRows" :key="a.id" class="crm-list-row">
          <div style="flex:1;min-width:0">
            <div class="crm-row-title">{{ a.label }}</div>
            <div class="crm-row-meta">{{ a.meta }}</div>
          </div>
          <span class="crm-badge">{{ a.when }}</span>
        </div>
      </div>

      <div class="crm-prize-badge" v-if="prizeResult">
        <div>
          <strong>Instant gagnant · −{{ prizeResult }} %</strong>
          <p class="crm-hint" style="margin:4px 0 0">sur la première commande · valable 30 jours</p>
        </div>
        <a
          v-if="waLink"
          class="crm-btn crm-btn-ok"
          :href="waLink"
          target="_blank"
          rel="noopener"
          @click="markSent('whatsapp')"
        >WhatsApp</a>
      </div>

      <div class="crm-card" v-if="fiche.updated_at || fiche.created_at">
        <p class="crm-hint" style="margin:0">
          <span v-if="fiche.created_at">Créé le {{ fmtDateTime(fiche.created_at) }}</span>
          <span v-if="fiche.created_at && fiche.updated_at"> · </span>
          <span v-if="fiche.updated_at">Mis à jour le {{ fmtDateTime(fiche.updated_at) }}</span>
        </p>
      </div>

      <div class="crm-btn-stack">
        <button type="button" class="crm-btn crm-btn-primary crm-btn-lg" @click="goQualify">Qualifier</button>
        <button type="button" class="crm-btn crm-btn-navy crm-btn-lg" @click="bookHotRdv" v-if="isHot">RDV immédiat</button>
        <button
          v-if="showAnnonceurCta"
          type="button"
          class="crm-btn crm-btn-soft"
          :disabled="validating"
          @click="validate"
        >{{ validating ? '…' : annonceurCtaLabel }}</button>
        <button type="button" class="crm-btn crm-btn-outline" @click="uploadCarte">Joindre / changer la photo carte</button>
      </div>
      <input ref="carteInput" class="crm-upload-input" type="file" accept="image/*" capture="environment" @change="onCarteFile" />
      <p v-if="msg" class="crm-hint" style="margin-top:10px">{{ msg }}</p>
    </div>
  </div>
</template>

<script>
import {
  displayName,
  prizeWhatsAppText,
  waHref,
  activityTimestamp,
  formatParisDateTime,
  formatSalonDate,
  formatCalendarDate,
  parisTodayIso,
  toParisInstant,
  displaySourceFlags,
  sourceLabel as sourceFlagLabel,
  salonReturnTo,
  salonReturnLabel,
  withFrom,
  appendNoteInterne,
  currentSalonAccountLabel
} from './crmSalonUtils'
import { invalidateJourneeCache } from './crmSalonCache'

export default {
  name: 'CrmSalonFiche',
  props: { salonMode: { type: Boolean, default: undefined } },
  inject: {
    crmSalonMode: { default: null }
  },
  data() {
    return {
      fiche: null,
      loading: true,
      msg: '',
      prizeResult: null,
      prizeId: null,
      validating: false,
      commentDraft: '',
      commentSaving: false
    }
  },
  computed: {
    backTo() {
      return salonReturnTo(this.$route, '/crm-salon')
    },
    backLabel() {
      return salonReturnLabel(this.backTo)
    },
    salonModeResolved() {
      if (typeof this.salonMode === 'boolean') return this.salonMode
      if (this.crmSalonMode) return !!this.crmSalonMode.salonMode
      return true
    },
    hasClientEco() {
      if (!this.fiche) return false
      return !!(
        this.fiche.utilisateur_id ||
        (Number(this.fiche.ca_global) || 0) > 0 ||
        (Number(this.fiche.orders_count) || 0) > 0 ||
        (this.fiche.orders && this.fiche.orders.length)
      )
    },
    name() { return displayName(this.fiche) },
    sourceFlags() {
      return displaySourceFlags(this.fiche)
    },
    isHot() {
      if (!this.fiche) return false
      return (this.fiche.copilot_score || 0) >= 4 || (this.fiche.interest || 0) >= 4
    },
    waLink() {
      if (!this.fiche || !this.prizeResult) return null
      return waHref(this.fiche.telephone || this.fiche.telephone2, prizeWhatsAppText(this.fiche.prenom, this.prizeResult))
    },
    // Mode Interne : uniquement si pas encore lié — ne jamais modifier utilisateurs existants
    showAnnonceurCta() {
      if (this.salonModeResolved || !this.fiche) return false
      if (!this.fiche.email) return false
      if (String(this.fiche.id || '').indexOf('u-') === 0 && !this.fiche.prospect_id) return false
      // Déjà client lié → pas de bouton (évite toute action sur utilisateurs)
      if (this.fiche.utilisateur_id || this.fiche.status === 'validated') return false
      return true
    },
    annonceurCtaLabel() {
      return 'Valider compte client'
    },
    activityRows() {
      if (!this.fiche) return []
      const rows = []
      const account = (email) => {
        if (!email) return ''
        const s = String(email)
        const at = s.indexOf('@')
        return at > 0 ? s.slice(0, at) : s
      }

      ;(this.fiche.visits || []).forEach((v) => {
        const stamp = activityTimestamp({
          updated_at: v.updated_at,
          visited_at: v.visited_at,
          at: v.visited_at,
          created_at: v.created_at
        })
        const passageMap = {
          visite: 'Visite',
          echange: 'Échange',
          rdv: 'Souhaite un RDV',
          proposition: 'Proposition'
        }
        rows.push({
          id: 'visit-' + v.id,
          label: passageMap[v.passage] || v.passage || 'Visite',
          meta: [account(v.created_by), v.origin].filter(Boolean).join(' · '),
          when: formatParisDateTime(stamp),
          ms: toParisInstant(stamp) || 0
        })
      })

      ;(this.fiche.rdvs || []).forEach((r) => {
        const slot = [
          r.rdv_day ? formatCalendarDate(r.rdv_day) : null,
          r.rdv_time || null
        ].filter(Boolean).join(' ')
        rows.push({
          id: 'rdv-' + r.id,
          label: 'RDV' + (r.confirmed ? ' confirmé' : ''),
          meta: [slot, r.channel, account(r.created_by)].filter(Boolean).join(' · '),
          when: r.created_at || r.updated_at
            ? formatParisDateTime(activityTimestamp({
              updated_at: r.updated_at,
              created_at: r.created_at
            }))
            : (slot || '—'),
          ms: toParisInstant(activityTimestamp({
            updated_at: r.updated_at,
            created_at: r.created_at,
            at: r.rdv_day && r.rdv_time ? (r.rdv_day + ' ' + r.rdv_time + ':00') : r.rdv_day
          })) || 0
        })
      })

      ;(this.fiche.prizes || []).forEach((p) => {
        const stamp = activityTimestamp({
          updated_at: p.updated_at,
          created_at: p.created_at,
          at: p.created_at
        })
        rows.push({
          id: 'prize-' + p.id,
          label: 'Remise ' + (p.prize_percent || '') + ' %',
          meta: [p.sent_channel ? ('envoyé · ' + p.sent_channel) : 'tirage', account(p.created_by)].filter(Boolean).join(' · '),
          when: formatParisDateTime(stamp),
          ms: toParisInstant(stamp) || 0
        })
      })

      // Qualification / dernière MAJ fiche (updated_at)
      if (this.fiche.updated_at && (this.fiche.pipeline === 'Qualifié' || this.fiche.updated_by)) {
        const stamp = this.fiche.updated_at
        rows.push({
          id: 'update-' + this.fiche.id,
          label: this.fiche.pipeline === 'Qualifié' ? 'Qualification' : 'Mise à jour',
          meta: [this.fiche.pipeline, account(this.fiche.updated_by)].filter(Boolean).join(' · '),
          when: formatParisDateTime(stamp),
          ms: toParisInstant(stamp) || 0
        })
      }

      rows.sort((a, b) => b.ms - a.ms)
      return rows
    }
  },
  watch: {
    '$route.params.id'() { this.load() }
  },
  mounted() { this.load() },
  methods: {
    sourceFlagLabel,
    goQualify() {
      if (!this.fiche) return
      const from = this.$route.query.from || this.backTo
      this.$router.push(withFrom(
        { path: '/crm-salon/qualifier', query: { id: String(this.fiche.id) } },
        from
      ))
    },
    async addComment(asAction) {
      const text = (this.commentDraft || '').trim()
      if (!text || !this.fiche) return
      this.commentSaving = true
      this.msg = ''
      try {
        const note = appendNoteInterne(
          this.fiche.note_interne,
          text,
          currentSalonAccountLabel(),
          asAction ? 'action' : null
        )
        const variables = {
          id: String(this.fiche.prospect_id || this.fiche.id),
          note_interne: note
        }
        if (asAction) {
          variables.todo_action = text
          variables.todo_closed = false
        }
        await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonUpsertProspect.gql'),
          variables
        })
        this.fiche.note_interne = note
        if (asAction) {
          this.fiche.todo_action = text
          this.fiche.todo_closed = false
          this.msg = 'Action ajoutée — visible dans À faire'
        } else {
          this.msg = 'Commentaire ajouté'
        }
        this.commentDraft = ''
        invalidateJourneeCache()
      } catch (e) {
        this.msg = e.message || 'Erreur'
      } finally {
        this.commentSaving = false
      }
    },
    fmtEuro(n) {
      const v = Number(n) || 0
      return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    },
    fmtDate(raw) {
      return formatSalonDate(raw)
    },
    fmtDateTime(raw) {
      return formatParisDateTime(raw)
    },
    async load() {
      this.loading = true
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonFiche.gql'),
          variables: { id: String(this.$route.params.id) },
          fetchPolicy: 'network-only'
        })
        this.fiche = data.crmSalonFiche
        if (this.fiche && this.fiche.prizes && this.fiche.prizes.length) {
          this.prizeResult = this.fiche.prizes[0].prize_percent
          this.prizeId = this.fiche.prizes[0].id
        } else {
          this.prizeResult = null
          this.prizeId = null
        }
      } catch (e) {
        this.msg = e.message || 'Erreur'
      } finally {
        this.loading = false
      }
    },
    async markSent(channel) {
      if (!this.prizeId) return
      try {
        await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonMarkPrizeSent.gql'),
          variables: { prize_id: String(this.prizeId), channel }
        })
      } catch (e) {}
    },
    async bookHotRdv() {
      const day = parisTodayIso()
      try {
        await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonBookRdv.gql'),
          variables: {
            prospect_id: String(this.fiche.prospect_id || this.fiche.id),
            rdv_day: day,
            rdv_time: '10:00',
            channel: 'whatsapp',
            note: 'Piste chaude stand'
          }
        })
        this.msg = 'RDV proposé pour aujourd’hui 10:00'
      } catch (e) {
        this.msg = e.message || 'Erreur RDV'
      }
    },
    async validate() {
      this.validating = true
      try {
        const { data } = await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonValidateProspect.gql'),
          variables: { prospect_id: String(this.fiche.prospect_id || this.fiche.id) }
        })
        this.msg = data.crmSalonValidateProspect.message
        await this.load()
      } catch (e) {
        this.msg = e.message || 'Validation impossible'
      } finally {
        this.validating = false
      }
    },
    uploadCarte() {
      this.$refs.carteInput && this.$refs.carteInput.click()
    },
    async onCarteFile(e) {
      const file = e.target.files && e.target.files[0]
      if (!file || !this.fiche) return
      try {
        const formData = new FormData()
        formData.append('prospect_id', this.fiche.prospect_id || this.fiche.id)
        formData.append('image', file)
        const token = localStorage.getItem('token_jwt')
        const res = await this.axios.post('https://api-auth-plf.wee-do-it.net/api/v1/crm-salon/upload-carte', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.data && res.data.carte_url) {
          this.fiche.carte_url = res.data.carte_url
          this.msg = 'Photo carte enregistrée'
        }
      } catch (err) {
        this.msg = 'Upload carte échoué'
      }
    }
  }
}
</script>
